import type { MyWorkBucketOverrides } from '@/pages/tasks/my-work/model'
import { isTaskBucketPlacementCurrent } from '@/pages/tasks/my-work/model'

import type {
  PersonalTaskBucket as TaskBucket,
  TasksPersonalPreferences,
  TaskWithAssignments,
} from '@scrambl/shared'

import { isTaskClosed } from './task-completion'

type WidgetBucket = TaskBucket | 'completedToday'

type MyTasksPreferences = TasksPersonalPreferences | null

const createEmptyTaskBuckets = (): Record<
  WidgetBucket,
  TaskWithAssignments[]
> => ({
  today: [],
  tomorrow: [],
  thisWeek: [],
  nextWeek: [],
  later: [],
  unscheduled: [],
  completedToday: [],
})

export function buildTaskBuckets({
  tasks,
  optimisticTasks,
  getTaskBucket,
  preferences,
  myWorkBucketOverrides,
}: {
  tasks: TaskWithAssignments[]
  optimisticTasks: Map<string, TaskWithAssignments>
  getTaskBucket: (taskId: string) => TaskBucket | null
  preferences: MyTasksPreferences
  myWorkBucketOverrides: MyWorkBucketOverrides
}): Record<WidgetBucket, TaskWithAssignments[]> {
  const buckets = createEmptyTaskBuckets()

  const allTasks = [...tasks, ...Array.from(optimisticTasks.values())]
  if (allTasks.length === 0) return buckets

  // Deduplicate tasks by ID to prevent the same task appearing multiple times
  const seen = new Set<string>()
  const uniqueTasks = allTasks.filter(task => {
    if (seen.has(task.id)) return false
    seen.add(task.id)
    return true
  })

  const now = new Date()
  now.setHours(0, 0, 0, 0)

  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const endOfWeek = new Date(now)
  endOfWeek.setDate(endOfWeek.getDate() + ((7 - endOfWeek.getDay()) % 7))

  const endOfNextWeek = new Date(endOfWeek)
  endOfNextWeek.setDate(endOfNextWeek.getDate() + 7)

  // Separate completed and incomplete tasks
  uniqueTasks.forEach(task => {
    // Check for completed/canceled tasks
    if (isTaskClosed(task)) {
      // Only show tasks completed today based on completedAt
      if (task.completedAt) {
        const completedDate = new Date(task.completedAt)
        completedDate.setHours(0, 0, 0, 0)
        if (completedDate.getTime() === now.getTime()) {
          buckets.completedToday.push(task)
        }
      }
      return
    }

    // Manually parked Done work (My Work placement) has no schedule bucket
    // in the widget — same treatment as parked tickets below.
    if (myWorkBucketOverrides[`TASK:${task.id}`] === 'done') {
      return
    }

    // Check for custom bucket assignment first (for incomplete tasks)
    const customBucket = getTaskBucket(task.id)
    if (
      customBucket &&
      buckets[customBucket] &&
      isTaskBucketPlacementCurrent(task.scheduledFor, customBucket)
    ) {
      buckets[customBucket].push(task)
      return
    }

    const schedulingDateValue = task.scheduledFor || task.dueDate

    if (!schedulingDateValue) {
      buckets.unscheduled.push(task)
    } else {
      const schedulingDate = new Date(schedulingDateValue)
      schedulingDate.setHours(0, 0, 0, 0)

      if (schedulingDate < now) {
        buckets.today.push(task)
      } else if (schedulingDate.getTime() === now.getTime()) {
        buckets.today.push(task)
      } else if (schedulingDate.getTime() === tomorrow.getTime()) {
        buckets.tomorrow.push(task)
      } else if (schedulingDate <= endOfWeek) {
        buckets.thisWeek.push(task)
      } else if (schedulingDate <= endOfNextWeek) {
        buckets.nextWeek.push(task)
      } else {
        buckets.later.push(task)
      }
    }
  })

  // Sort tasks within each bucket - custom ordered first, then by due date
  // Skip completedToday as it uses a different sort
  ;(Object.keys(buckets) as WidgetBucket[]).forEach(bucketKey => {
    if (bucketKey === 'completedToday') {
      // Sort completed tasks by most recently completed first
      buckets.completedToday.sort((a, b) => {
        const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0
        const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0
        return bTime - aTime
      })
      return
    }

    const order = preferences?.order?.[bucketKey as TaskBucket] ?? []

    buckets[bucketKey].sort((a, b) => {
      const aIndex = order.indexOf(a.id)
      const bIndex = order.indexOf(b.id)

      // If both have custom positions, sort by position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex
      }

      // Tasks with custom positions come first
      if (aIndex !== -1) return -1
      if (bIndex !== -1) return 1

      // Default sort by due date
      const aDue = a.dueDate
        ? new Date(a.dueDate).getTime()
        : Number.MAX_SAFE_INTEGER
      const bDue = b.dueDate
        ? new Date(b.dueDate).getTime()
        : Number.MAX_SAFE_INTEGER
      if (aDue !== bDue) return aDue - bDue
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    })
  })

  return buckets
}
